import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface PhotoUploadResult {
  firebaseStoragePath: string;
  downloadUrl: string;
}

/**
 * Maps the logical section name used in the app to the folder name used in
 * Firebase Storage. Matches the mobile convention (singular lowercase).
 *
 * Mobile path structure (confirmed from API):
 *   inspections/{tenantId}/{sessionUuid}/photos/{section}/{fileUuid}.ext
 *   inspections/{tenantId}/{sessionUuid}/voice-notes/{fileUuid}.m4a
 */
const SECTION_FOLDER_MAP: Record<string, string> = {
  general:      'general',
  pest:         'pest',
  disease:      'disease',
  deficiency:   'deficiency',
  // Allow uppercase section names from the InspectionPhoto.section enum
  GENERAL:      'general',
  PEST:         'pest',
  DISEASE:      'disease',
  DEFICIENCY:   'deficiency',
  // Legacy plural names that may appear from old call sites
  pests:        'pest',
  diseases:     'disease',
  deficiencies: 'deficiency',
};

function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function fileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? '.' + parts[parts.length - 1].toLowerCase() : '';
}

@Injectable({
  providedIn: 'root'
})
export class InspectionPhotoService {

  constructor(private storage: Storage) {}

  /**
   * Uploads a photo to Firebase Storage using the same path structure as mobile:
   *   inspections/{tenantId}/{sessionUuid}/photos/{section}/{fileUuid}.ext
   *
   * @param file        The image file to upload
   * @param tenantId    The tenant ID (numeric)
   * @param reportId    The report ID (UUID string). If empty string, a temporary
   *                    session UUID is used — the path is updated on the next upload
   *                    once the report has been saved and a real ID is available.
   * @param section     Section name: 'general' | 'pest' | 'disease' | 'deficiency'
   *                    (also accepts legacy plural / uppercase variants)
   * @param sessionUuid Optional: pass a stable UUID for this upload session so all
   *                    photos for one report land in the same folder. If omitted,
   *                    a new UUID is generated per upload.
   */
  uploadPhoto(
    file: File,
    tenantId: number,
    reportId: string,
    section: string,
    sessionUuid?: string
  ): Observable<PhotoUploadResult> {
    const folder = SECTION_FOLDER_MAP[section] ?? section.toLowerCase();
    const uuid = sessionUuid ?? (reportId && reportId.length > 0 ? reportId : generateUuid());
    const fileUuid = generateUuid();
    const ext = fileExtension(file.name);
    const path = `inspections/${tenantId}/${uuid}/photos/${folder}/${fileUuid}${ext}`;
    const fileRef = ref(this.storage, path);

    return from(uploadBytes(fileRef, file)).pipe(
      switchMap(() => from(getDownloadURL(fileRef))),
      switchMap(downloadUrl => [{ firebaseStoragePath: path, downloadUrl }])
    );
  }

  /**
   * Uploads multiple photos in sequence and returns all results.
   */
  uploadPhotos(
    files: File[],
    tenantId: number,
    reportId: string,
    section: string,
    sessionUuid?: string
  ): Observable<PhotoUploadResult>[] {
    // Share the same sessionUuid across all uploads in this batch
    const uuid = sessionUuid ?? (reportId && reportId.length > 0 ? reportId : generateUuid());
    return files.map(file => this.uploadPhoto(file, tenantId, reportId, section, uuid));
  }
}
